import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// Services
import { UserStaffService } from '../../../../Services/User/Staff/user-staff.service';
import { NotificationService } from '../../../../Services/notification.service';
import { UserAccessService } from '../../../../Services/UserAccess/user-access.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { AuthService } from '../../../../Services/auth.service';

interface MenuOption {
  lable: string;
  path: string;
  visible: boolean;
  submenu?: MenuOption[];
  key?: string;
  updatePermission?: string;
  deletePermission?: string;
}

@Component({
  selector: 'app-gst-admin-access-control',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gst-admin-access-control.component.html',
})
export class GstAdminAccessControlComponent implements OnInit, OnChanges {

  @Input() staffId: number | null = null;
  @Output() backToStaffList = new EventEmitter<void>();

  activeUserId: number | null = null;
  activeUserName: string | null = null;
  activeUserImage: string | null = null;
  activeUserLastName: string | null = null;
  isLoading = false;

  selectedMainPaths: string[] = [];
  selectedSubPaths: string[] = [];
  selectedUpdatePaths: string[] = [];
  selectedDeletePaths: string[] = [];

  activeMenuIndex: number = 0;
  showMobileDetails: boolean = false;

  // GST-specific Navbar Options
  navbarOptions: MenuOption[] = [
    {
      lable: 'GST Bill',
      path: '/invoice-gst-panel',
      visible: true,
      submenu: [
        { path: '/invoice-gst-panel', lable: 'GST Invoice List', visible: true, updatePermission: 'gst_invoices_update', deletePermission: 'gst_invoices_delete' },
        { path: '/invoice-gst', lable: 'GST Invoice Preview', visible: true }
      ]
    },
    {
      lable: 'Purchase Bill',
      path: '/purchase-bill',
      visible: true,
      submenu: [
        { path: '/purchase-bill', lable: 'Purchase Bill Form', visible: true, updatePermission: 'purchase_bills_update', deletePermission: 'purchase_bills_delete' }
      ]
    },
    {
      lable: 'Reports',
      path: '/purchase-report',
      visible: true,
      submenu: [
        { path: '/purchase-report', lable: 'Purchase Bills', visible: true, updatePermission: 'purchase_bills_update', deletePermission: 'purchase_bills_delete' },
        { path: '/gst-bill-report', lable: 'GST Bills', visible: true, updatePermission: 'gst_invoices_update', deletePermission: 'gst_invoices_delete' }
      ]
    },
    {
      lable: 'Customers',
      path: '/gst-customer-list',
      visible: true,
      submenu: [
        { path: '/gst-customer-list', lable: 'GST Customer List', visible: true, updatePermission: 'users_list_update', deletePermission: 'users_list_delete' }
      ]
    },
    {
      lable: 'Settings',
      path: '/gst-settings',
      visible: true,
      submenu: [
        { path: '/gst-settings', lable: 'GST Settings', visible: true }
      ]
    },
    {
      lable: 'Accounts',
      path: '/accounts',
      visible: true,
      submenu: [
        { path: '/accounts', lable: 'Accounts List', visible: true, updatePermission: 'accounts_update', deletePermission: 'accounts_delete' },
        { path: '/account-ledger', lable: 'Account Ledger', visible: true },
      ]
    },
    {
      lable: 'Daybook',
      path: '/daybook',
      visible: true,
      submenu: [
        { path: '/daybook', lable: 'Daybook List', visible: true }
      ]
    },
    {
      lable: 'Trial Balance',
      path: '/trail-balance',
      visible: true,
      submenu: [
        { path: '/trail-balance', lable: 'Trial Balance List', visible: true }
      ]
    },
    {
      lable: 'Profile',
      path: '/ca-account',
      visible: true,
      submenu: [{ path: '/ca-account', lable: 'My Profile ', visible: true, updatePermission: 'user_staff_update', deletePermission: 'user_staff_delete' }],

    }
  ];

  constructor(
    private userStaffService: UserStaffService,
    private userAccessService: UserAccessService,
    private notificationService: NotificationService,
    private firmSelectionService: FirmSelectionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    if (this.authService.getUserRole() === 'Staff') {
      this.goBack();
      return;
    }
    if (this.staffId) {
      this.activeUserId = this.staffId;
      this.fetchUserName();
      this.fetchPermissions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.authService.getUserRole() === 'Staff') {
      this.goBack();
      return;
    }
    if (changes['staffId'] && changes['staffId'].currentValue) {
      this.activeUserId = changes['staffId'].currentValue;
      this.fetchUserName();
      this.fetchPermissions();
    }
  }

  fetchUserName() {
    if (!this.activeUserId) return;
    this.userStaffService.getUserStaffById(this.activeUserId).subscribe({
      next: (res: any) => {
        this.activeUserName = res.data?.name || res.name || 'Unknown Staff';
        this.activeUserLastName = res.data?.user_last_name;
        this.activeUserImage = res.data?.documents?.[0]?.doc_user_img;
      },
      error: (err) => {
        console.error('Failed to fetch user details', err);
        this.activeUserName = 'Unknown User';
      }
    });
  }

  fetchPermissions() {
    if (!this.activeUserId) return;

    this.isLoading = true;

    this.userAccessService.getUserAccess(this.activeUserId).subscribe({
      next: (res: any) => {
        const data = res.data;
        if (data) {
          this.selectedMainPaths = data.mainmenu_visibility || [];
          this.selectedSubPaths = data.submenu_visibility || [];
          this.selectedUpdatePaths = data.update_access || [];
          this.selectedDeletePaths = data.delete_access || [];
        } else {
          this.selectedMainPaths = [];
          this.selectedSubPaths = [];
          this.selectedUpdatePaths = [];
          this.selectedDeletePaths = [];
        }
        this.isLoading = false;
        this.activeMenuIndex = 0;
        this.showMobileDetails = false;
      },
      error: (err) => {
        console.error('Error fetching permissions:', err);
        if (err.status === 401) {
          this.notificationService.showError('Unauthorized: Please Login Again.', 'Error');
        } else {
          this.notificationService.showError('Could not fetch permissions.', 'Error');
        }
        this.isLoading = false;
      }
    });
  }

  savePermissions() {
    if (this.authService.getUserRole() === 'Staff') {
      this.notificationService.showError('Permission Denied', 'Error');
      return;
    }
    if (!this.activeUserId) return;

    const isConfirmed = window.confirm(
      'Are you sure you want to save these permissions?'
    );

    if (!isConfirmed) {
      return;
    }

    const selectedFirm = this.firmSelectionService.getselectedFirmName();
    const firmId = selectedFirm?.id || 1;

    const payload = {
      user_id: this.activeUserId,
      firm_id: firmId,
      mainmenu_visibility: this.selectedMainPaths,
      submenu_visibility: this.selectedSubPaths,
      update_access: this.selectedUpdatePaths,
      delete_access: this.selectedDeletePaths
    };

    this.userAccessService.saveUserAccess(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          'Permissions saved successfully!',
          'Success'
        );
      },
      error: (err) => {
        console.error('Error saving:', err);

        if (err.status === 401) {
          this.notificationService.showError(
            'Unauthorized: Please login again.',
            'Error'
          );
        } else {
          this.notificationService.showError(
            'Failed to save permissions.',
            'Error'
          );
        }
      }
    });
  }

  goBack() {
    this.backToStaffList.emit();
  }

  // --- HELPER UI METHODS ---

  setActiveMenu(index: number) {
    this.activeMenuIndex = index;
    this.showMobileDetails = true;
  }

  backToMobileList() {
    this.showMobileDetails = false;
  }

  toggleMain(path: string, event: any) {
    const checked = event.target.checked;

    if (checked) {
      if (!this.selectedMainPaths.includes(path)) {
        this.selectedMainPaths.push(path);
      }
    } else {
      this.selectedMainPaths = this.selectedMainPaths.filter(p => p !== path);

      const mainMenu = this.navbarOptions.find(m => m.path === path);
      if (mainMenu && mainMenu.submenu?.length) {
        const submenuPaths = mainMenu.submenu.map(s => s.path);
        this.selectedSubPaths = this.selectedSubPaths.filter(p => !submenuPaths.includes(p));
      }
    }
  }

  isMainChecked(path: string): boolean {
    return this.selectedMainPaths.includes(path);
  }

  isSubChecked(path: string): boolean {
    return this.selectedSubPaths.includes(path);
  }

  areAllSubmenusChecked(submenus: MenuOption[] = []): boolean {
    if (!submenus.length) return false;
    return submenus.every(sub =>
      this.selectedSubPaths.includes(sub.path)
    );
  }

  isAnySubmenuChecked(submenus: MenuOption[] = []): boolean {
    return submenus.some(sub =>
      this.selectedSubPaths.includes(sub.path)
    );
  }

  toggleSub(sub: MenuOption, event: any) {
    const activeItem = this.navbarOptions[this.activeMenuIndex];
    if (!activeItem) return;

    if (event.target.checked) {
      if (!this.selectedSubPaths.includes(sub.path)) {
        this.selectedSubPaths.push(sub.path);
      }

      if (
        sub.updatePermission &&
        !this.selectedUpdatePaths.includes(sub.updatePermission)
      ) {
        this.selectedUpdatePaths.push(sub.updatePermission);
      }
    } else {
      this.selectedSubPaths = this.selectedSubPaths.filter(p => p !== sub.path);

      if (sub.updatePermission) {
        this.selectedUpdatePaths = this.selectedUpdatePaths.filter(p => p !== sub.updatePermission);
      }

      if (sub.deletePermission) {
        this.selectedDeletePaths = this.selectedDeletePaths.filter(p => p !== sub.deletePermission);
      }
    }

    const anyChecked = this.isAnySubmenuChecked(activeItem.submenu || []);
    const mainPath = activeItem.path;

    if (anyChecked && !this.selectedMainPaths.includes(mainPath)) {
      this.selectedMainPaths.push(mainPath);
    }

    if (!anyChecked) {
      this.selectedMainPaths = this.selectedMainPaths.filter(p => p !== mainPath);
    }
  }

  toggleAllSubmenus(submenus: MenuOption[], event: any) {
    const activeItem = this.navbarOptions[this.activeMenuIndex];
    if (!activeItem) return;

    const mainPath = activeItem.path;

    if (event.target.checked) {
      submenus.forEach(sub => {
        if (!this.selectedSubPaths.includes(sub.path)) {
          this.selectedSubPaths.push(sub.path);
        }
      });

      if (!this.selectedMainPaths.includes(mainPath)) {
        this.selectedMainPaths.push(mainPath);
      }
    } else {
      const submenuPaths = submenus.map(s => s.path);
      this.selectedSubPaths = this.selectedSubPaths.filter(p => !submenuPaths.includes(p));
      this.selectedMainPaths = this.selectedMainPaths.filter(p => p !== mainPath);
    }
  }

  toggleUpdate(permission: string | undefined, event: any) {
    if (!permission) return;

    if (event.target.checked) {
      if (!this.selectedUpdatePaths.includes(permission)) {
        this.selectedUpdatePaths.push(permission);
      }
    } else {
      this.selectedUpdatePaths = this.selectedUpdatePaths.filter(p => p !== permission);
    }
  }

  toggleDelete(permission: string | undefined, event: any) {
    if (!permission) return;

    if (event.target.checked) {
      if (!this.selectedDeletePaths.includes(permission)) {
        this.selectedDeletePaths.push(permission);
      }
    } else {
      this.selectedDeletePaths = this.selectedDeletePaths.filter(p => p !== permission);
    }
  }

  isUpdateChecked(permission?: string): boolean {
    if (!permission) return false;
    return this.selectedUpdatePaths.includes(permission);
  }

  isDeleteChecked(permission?: string): boolean {
    if (!permission) return false;
    return this.selectedDeletePaths.includes(permission);
  }

  shouldShowActionPermissions(path: string): boolean {
    return path.startsWith('/purchase-report') ||
      path.startsWith('/gst-bill-report') ||
      path.startsWith('/gst-customer-list') ||
      path.startsWith('/gst-settings') ||
      path.startsWith('/accounts') ||
      path.startsWith('/daybook') ||
      path.startsWith('/trail-balance') ||
      path.startsWith('/ca-account') ||
      path === '/invoice-gst-panel' ||
      path === '/purchase-bill';
  }
}
